'use client';

import { useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { TabsPath } from 'components/commercetools-ui/organisms/account/types/types';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useMyPhotos from 'helpers/hooks/useMyPhotos';
import usePath from 'helpers/hooks/usePath';
import { desktop } from 'helpers/utils/screensizes';
import { useFeatures } from 'providers/feature-flag';
import WidgetDrawer from './components/widget-drawer';
import WidgetModal from './components/widget-modal';
import WidgetPill from './components/widget-pill';
import { useAlbumTitle } from './hooks/useAlbumTitle';
import { useAutoReveal } from './hooks/useAutoReveal';
import { MyPhotosWidgetProps } from './types';

const HIDE_ON_PATH_PREFIXES = ['/checkout'];
/** The My Photos account tab lists the same photos, so the widget is redundant there. */
const HIDE_ON_ACCOUNT_HASHES = [TabsPath.MY_PHOTOS as string];

const MyPhotosWidget = ({ pillIcon, ...config }: MyPhotosWidgetProps) => {
  const { enableMyPhotos } = useFeatures();
  const isMyPhotosEnabled = enableMyPhotos();
  const { photos, isLoading, removePhoto } = useMyPhotos({ scope: 'sessionOnly' });
  const [isDesktopSize] = useMediaQuery(desktop);
  const { pathWithoutQuery } = usePath();
  const [isOpen, setIsOpen] = useState(false);
  const albumTitle = useAlbumTitle();

  const searchParams = useSearchParams();

  const isHiddenRoute =
    HIDE_ON_PATH_PREFIXES.some((prefix) => pathWithoutQuery === prefix || pathWithoutQuery.startsWith(`${prefix}/`)) ||
    HIDE_ON_ACCOUNT_HASHES.includes(searchParams?.get('hash') ?? '');

  const isVisible = isMyPhotosEnabled && !isHiddenRoute && !isLoading && photos.length > 0;
  // Ids of the current photos, memoized so the reveal only re-arms when a genuinely
  // new photo is brought in — not on plain navigation, a refresh, or a removal.
  const photoIds = useMemo(() => photos.map((photo) => photo.id), [photos]);
  const { expanded, cancel } = useAutoReveal(isVisible, photoIds);

  const handleOpen = () => {
    cancel();
    setIsOpen(true);
  };

  const pillStyle = Array.isArray(config.widgetPillStyle)
    ? (config.widgetPillStyle[0] ?? {})
    : (config.widgetPillStyle ?? {});
  const modalHeadingStyle = Array.isArray(config.widgetModalHeadingStyle)
    ? (config.widgetModalHeadingStyle[0] ?? {})
    : (config.widgetModalHeadingStyle ?? {});

  if (!isVisible) return null;

  return (
    <>
      {!isOpen && (
        <WidgetPill
          count={photos.length}
          icon={pillIcon}
          expanded={expanded}
          onOpen={handleOpen}
          pillHeading={config.pillHeading}
          pillSubheading={config.pillSubheading}
          pillBgColor={pillStyle.pillBgColor}
          pillHeadingTextColor={pillStyle.pillHeadingTextColor}
          pillSubheadingTextColor={pillStyle.pillSubheadingTextColor}
          badgeBgColor={pillStyle.badgeBgColor}
          badgeTextColor={pillStyle.badgeTextColor}
          pillHeadingTextFont={pillStyle.pillHeadingTextFont}
          pillSubheadingTextFont={pillStyle.pillSubheadingTextFont}
          pillHeadingTextFontWeight={pillStyle.pillHeadingTextFontWeight}
          pillSubheadingTextFontWeight={pillStyle.pillSubheadingTextFontWeight}
        />
      )}

      {isDesktopSize ? (
        <WidgetModal
          isOpen={isOpen}
          photos={photos}
          onHide={() => setIsOpen(false)}
          removePhoto={removePhoto}
          heading={config.heading}
          albumTitle={albumTitle}
          previewHeading={config.previewHeading}
          previewPhotoAriaLabel={config.previewPhotoAriaLabel}
          previousPhotoAriaLabel={config.previousPhotoAriaLabel}
          nextPhotoAriaLabel={config.nextPhotoAriaLabel}
          previewPhotoErrorMessage={config.previewPhotoErrorMessage}
          removeBtnCtaLabel={config.removeBtnCtaLabel}
          removePhotoSuccessMessage={config.removePhotoSuccessMessage}
          removePhotoErrorMessage={config.removePhotoErrorMessage}
          goBackBtnCtaLabel={config.goBackBtnCtaLabel}
          cancelBtnCtaLabel={config.cancelBtnCtaLabel}
          confirmModalTitle={config.confirmModalTitle}
          confirmModalDescription={config.confirmModalDescription}
          hideBtnCtaLabel={config.hideBtnCtaLabel}
          headingFontSize={modalHeadingStyle.desktopHeadingFontSize}
          headingColor={modalHeadingStyle.desktopHeadingFontColor}
          headingFontWeight={modalHeadingStyle.desktopHeadingFontWeight}
          hideButtonBgColor={config.hideButtonBgColor}
          hideButtonTextColor={config.hideButtonTextColor}
        />
      ) : (
        <WidgetDrawer
          isOpen={isOpen}
          photos={photos}
          onHide={() => setIsOpen(false)}
          removePhoto={removePhoto}
          heading={config.heading}
          albumTitle={albumTitle}
          previewHeading={config.previewHeading}
          previewPhotoAriaLabel={config.previewPhotoAriaLabel}
          previousPhotoAriaLabel={config.previousPhotoAriaLabel}
          nextPhotoAriaLabel={config.nextPhotoAriaLabel}
          previewPhotoErrorMessage={config.previewPhotoErrorMessage}
          removeBtnCtaLabel={config.removeBtnCtaLabel}
          removePhotoSuccessMessage={config.removePhotoSuccessMessage}
          removePhotoErrorMessage={config.removePhotoErrorMessage}
          goBackBtnCtaLabel={config.goBackBtnCtaLabel}
          backBtnCtaLabel={config.backBtnCtaLabel}
          cancelBtnCtaLabel={config.cancelBtnCtaLabel}
          confirmModalTitle={config.confirmModalTitle}
          confirmModalDescription={config.confirmModalDescription}
          hideBtnCtaLabel={config.hideBtnCtaLabel}
          selectedPhotosAriaLabel={config.selectedPhotosAriaLabel}
          headingFontSize={modalHeadingStyle.mobileHeadingFontSize ?? modalHeadingStyle.desktopHeadingFontSize}
          headingColor={modalHeadingStyle.mobileHeadingFontColor ?? modalHeadingStyle.desktopHeadingFontColor}
          headingFontWeight={modalHeadingStyle.mobileHeadingFontWeight ?? modalHeadingStyle.desktopHeadingFontWeight}
          hideButtonBgColor={config.hideButtonBgColor}
          hideButtonTextColor={config.hideButtonTextColor}
        />
      )}
    </>
  );
};

export default MyPhotosWidget;
